/** Shared pieces of the email marketing pages: error handling, block editor, email preview. */
import React, { useState } from 'react';
import { Button, Card, Checkbox, ColorPicker, Dropdown, Empty, Input, InputNumber, Modal, Segmented, Select, Space, Spin, Tooltip, Typography, message } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { t as tt, useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import { listOf, toastErr, useSite } from './common';
import { ProductPicker, useCollections } from './ShopCommon';

const { Text } = Typography;

/**
 * Marketing errors: 402 tm_marketing_plan_required / tm_quota_insufficient → link to TM;
 * 409 sending_domain_required → open the sending-domain tab. Returns true when handled.
 */
export const mktError = (e, { goDomain } = {}) => {
    const b = e?.body || {};
    const code = b.error_code || b.code;
    const key = 'yc-mkt-err';
    if (code === 'tm_marketing_plan_required' || code === 'tm_quota_insufficient') {
        const url = b.topup_url || '/tm/streams';
        message.warning({
            key, duration: 8,
            content: <Space>{b.error || tt('Activate an email sending marketing plan first.')}<Button size="small" type="primary" onClick={() => { message.destroy(key); window.location.href = userPath(url.startsWith('/') ? url : `/${url}`); }}>{tt('Open email sending')}</Button></Space>,
        });
        return true;
    }
    if (code === 'sending_domain_required') {
        message.warning({
            key, duration: 8,
            content: <Space>{b.error || tt('Connect and verify your sending domain first.')}{goDomain && <Button size="small" type="primary" onClick={() => { message.destroy(key); goDomain(); }}>{tt('Sending domain')}</Button>}</Space>,
        });
        return true;
    }
    toastErr(e);
    return false;
};

/** HTML email preview in a sandboxed iframe (no scripts) */
export const EmailPreview = ({ html, height = 560, width = '100%' }) => (
    <iframe title="email-preview" sandbox="" srcDoc={html || ''} style={{ width, maxWidth: '100%', height, border: '1px solid var(--ant-color-border-secondary, #f0f0f0)', borderRadius: 6, background: '#fff' }} />
);

export const PreviewModal = ({ open, onClose, loader }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [device, setDevice] = useState('desktop');
    React.useEffect(() => {
        if (!open) return;
        setData(null);
        loader().then(setData).catch((e) => { toastErr(e); onClose(); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    return (
        <Modal open={open} onCancel={onClose} footer={null} width={720} title={data?.subject || t('Preview')} destroyOnHidden>
            <Segmented size="small" value={device} onChange={setDevice} style={{ marginBottom: 8 }} options={[{ value: 'desktop', label: t('Desktop') }, { value: 'mobile', label: t('Mobile') }]} />
            {!data ? <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div> : <div style={{ textAlign: 'center' }}><EmailPreview html={data.html} width={device === 'mobile' ? 380 : '100%'} /></div>}
        </Modal>
    );
};

/** test-send modal: up to 5 addresses */
export const TestModal = ({ open, onClose, onSend }) => {
    const { t } = useTranslation();
    const [to, setTo] = useState('');
    const [busy, setBusy] = useState(false);
    const send = async () => {
        const list = to.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean).slice(0, 5);
        if (!list.length) return;
        setBusy(true);
        try { await onSend(list); message.success(t('Test sent')); onClose(); } catch (_) { /* reported */ } finally { setBusy(false); }
    };
    return (
        <Modal open={open} onCancel={onClose} onOk={send} confirmLoading={busy} okText={t('Send test')} title={t('Send test')} destroyOnHidden>
            <Input autoFocus value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" onPressEnter={send} />
        </Modal>
    );
};

// ---------------------------------------------------------------- block editor

export const BLOCK_TYPES = [
    { type: 'heading', label: 'Heading', make: () => ({ type: 'heading', text: '', align: 'left' }) },
    { type: 'text', label: 'Text', make: () => ({ type: 'text', text: '', align: 'left' }) },
    { type: 'image', label: 'Image', make: () => ({ type: 'image', src: '', alt: '', url: '', width: 600 }) },
    { type: 'button', label: 'Button', make: () => ({ type: 'button', label: 'Shop now', url: '', color: '', align: 'center' }) },
    { type: 'products', label: 'Products', make: () => ({ type: 'products', product_ids: [], collection_id: null, limit: 4, columns: 2, show_price: true, button_label: '' }) },
    { type: 'discount', label: 'Discount code', make: () => ({ type: 'discount', template_id: null, title: '', text: '', expires_days: 14 }) },
    { type: 'divider', label: 'Divider', make: () => ({ type: 'divider' }) },
    { type: 'spacer', label: 'Spacer', make: () => ({ type: 'spacer', height: 24 }) },
    { type: 'social', label: 'Social links', make: () => ({ type: 'social', links: [] }) },
    { type: 'cart', label: 'Cart items', make: () => ({ type: 'cart', button_label: '' }) },
    { type: 'order', label: 'Order items', make: () => ({ type: 'order' }) },
    { type: 'post', label: 'Blog post', make: () => ({ type: 'post', button_label: '' }) },
];
const NETWORKS = ['facebook', 'instagram', 'x', 'tiktok', 'youtube', 'pinterest', 'linkedin', 'website'];

const AlignPick = ({ value, onChange }) => (
    <Segmented size="small" value={value || 'left'} onChange={onChange} options={[{ value: 'left', label: '⇤' }, { value: 'center', label: '↔' }, { value: 'right', label: '⇥' }]} />
);

const BlockFields = ({ b, set, discounts, collections }) => {
    const { t } = useTranslation();
    const [picking, setPicking] = useState(false);
    switch (b.type) {
        case 'heading':
            return <Space direction="vertical" style={{ width: '100%' }}><Input value={b.text} onChange={(e) => set({ text: e.target.value })} placeholder={t('Heading')} style={{ fontWeight: 600 }} /><AlignPick value={b.align} onChange={(align) => set({ align })} /></Space>;
        case 'text':
            return (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Input.TextArea value={b.text} onChange={(e) => set({ text: e.target.value })} autoSize={{ minRows: 3, maxRows: 14 }}
                        placeholder={t('Hi {{first_name}}, …')} />
                    <Space><AlignPick value={b.align} onChange={(align) => set({ align })} /><Text type="secondary" style={{ fontSize: 12 }}>**bold** · [label](https://…)</Text></Space>
                </Space>
            );
        case 'image':
            return (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Input value={b.src} onChange={(e) => set({ src: e.target.value })} placeholder="https://…/image.jpg" addonBefore={t('Image', { context: 'shop' })} />
                    <Space.Compact style={{ width: '100%' }}>
                        <Input value={b.alt} onChange={(e) => set({ alt: e.target.value })} placeholder={t('Alt text')} />
                        <Input value={b.url} onChange={(e) => set({ url: e.target.value })} placeholder={t('Link (optional)')} />
                        <InputNumber value={b.width} min={40} max={600} onChange={(width) => set({ width })} addonAfter="px" style={{ width: 130 }} />
                    </Space.Compact>
                </Space>
            );
        case 'button':
            return (
                <Space wrap>
                    <Input value={b.label} onChange={(e) => set({ label: e.target.value })} placeholder={t('Label')} style={{ width: 160 }} />
                    <Input value={b.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://…" style={{ width: 260 }} />
                    <ColorPicker value={b.color || '#111827'} disabledAlpha onChange={(c) => set({ color: c.toHexString() })} />
                    <AlignPick value={b.align || 'center'} onChange={(align) => set({ align })} />
                </Space>
            );
        case 'products':
            return (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap>
                        <Select allowClear placeholder={t('Collection')} style={{ width: 200 }} value={b.collection_id || undefined}
                            onChange={(v) => set({ collection_id: v || null })} options={collections.map((c) => ({ value: c.id, label: c.title }))} />
                        <Text type="secondary">{t('or')}</Text>
                        <Button size="small" onClick={() => setPicking(true)}>{b.product_ids?.length ? t('{{n}} products', { n: b.product_ids.length }) : t('Choose products')}</Button>
                    </Space>
                    <Space wrap>
                        <InputNumber size="small" min={1} max={12} value={b.limit} onChange={(limit) => set({ limit })} addonBefore={t('Show')} style={{ width: 120 }} />
                        <InputNumber size="small" min={1} max={3} value={b.columns} onChange={(columns) => set({ columns })} addonBefore={t('Columns')} style={{ width: 140 }} />
                        <Checkbox checked={b.show_price !== false} onChange={(e) => set({ show_price: e.target.checked })}>{t('Show price')}</Checkbox>
                        <Input size="small" value={b.button_label} onChange={(e) => set({ button_label: e.target.value })} placeholder={t('Button label')} style={{ width: 140 }} />
                    </Space>
                    <ProductPicker open={picking} onClose={() => setPicking(false)} initial={(b.product_ids || []).map((id) => ({ id }))}
                        onOk={(list) => { set({ product_ids: (list || []).map((p) => p.id).slice(0, 12) }); setPicking(false); }} />
                </Space>
            );
        case 'discount':
            return (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap>
                        <Select placeholder={t('Discount template')} style={{ width: 240 }} value={b.template_id || undefined} onChange={(template_id) => set({ template_id })}
                            options={discounts.map((d) => ({ value: d.id, label: d.code }))} notFoundContent={t('Create a discount first')} />
                        <InputNumber min={1} max={365} value={b.expires_days} onChange={(expires_days) => set({ expires_days })} addonAfter={t('days')} style={{ width: 130 }} />
                        <Tooltip title={t('Each recipient gets a single-use code copied from this discount')}><Text type="secondary">?</Text></Tooltip>
                    </Space>
                    <Input value={b.title} onChange={(e) => set({ title: e.target.value })} placeholder={t('Title')} />
                    <Input value={b.text} onChange={(e) => set({ text: e.target.value })} placeholder={t('Text')} />
                </Space>
            );
        case 'spacer':
            return <InputNumber min={4} max={120} value={b.height} onChange={(height) => set({ height })} addonAfter="px" />;
        case 'social':
            return (
                <Space direction="vertical" style={{ width: '100%' }}>
                    {(b.links || []).map((l, i) => (
                        <Space.Compact key={i} style={{ width: '100%' }}>
                            <Select value={l.network} style={{ width: 130 }} onChange={(network) => set({ links: b.links.map((x, j) => (j === i ? { ...x, network } : x)) })} options={NETWORKS.map((n) => ({ value: n, label: n }))} />
                            <Input value={l.url} placeholder="https://…" onChange={(e) => set({ links: b.links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} />
                            <Button icon={<DeleteOutlined />} onClick={() => set({ links: b.links.filter((_, j) => j !== i) })} />
                        </Space.Compact>
                    ))}
                    <Button size="small" icon={<PlusOutlined />} onClick={() => set({ links: [...(b.links || []), { network: 'instagram', url: '' }] })} disabled={(b.links || []).length >= 10}>{t('Add link')}</Button>
                </Space>
            );
        case 'cart':
        case 'post':
            return <Input value={b.button_label} onChange={(e) => set({ button_label: e.target.value })} placeholder={t('Button label')} style={{ width: 240 }} />;
        default:
            return null;
    }
};

/** content = {blocks: []}; onChange(content) */
export const BlockEditor = ({ value, onChange, types }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const collections = useCollections();
    const [discounts, setDiscounts] = useState([]);
    React.useEffect(() => { api.get('/discounts', { per_page: 200 }, { skipGlobalErrorToast: true }).then((d) => setDiscounts(listOf(d))).catch(() => {}); }, [api]);
    const blocks = value?.blocks || [];
    const setBlocks = (next) => onChange({ blocks: next });
    const allowed = BLOCK_TYPES.filter((x) => !types || types.includes(x.type));
    const addMenu = (at) => ({
        items: allowed.map((x) => ({ key: x.type, label: t(x.label) })),
        onClick: ({ key }) => { const n = [...blocks]; n.splice(at, 0, allowed.find((x) => x.type === key).make()); setBlocks(n); },
    });
    const mv = (i, d) => { const n = [...blocks]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); setBlocks(n); };
    return (
        <div>
            {!blocks.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No blocks yet')} />}
            {blocks.map((b, i) => (
                <Card key={i} size="small" style={{ marginBottom: 8 }}
                    title={<Text type="secondary" style={{ fontSize: 12 }}>{t(BLOCK_TYPES.find((x) => x.type === b.type)?.label || b.type)}</Text>}
                    extra={(
                        <Space size={0}>
                            <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => mv(i, -1)} />
                            <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === blocks.length - 1} onClick={() => mv(i, 1)} />
                            <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => { const n = [...blocks]; n.splice(i + 1, 0, JSON.parse(JSON.stringify(b))); setBlocks(n); }} />
                            <Dropdown menu={addMenu(i + 1)} trigger={['click']}><Button size="small" type="text" icon={<PlusOutlined />} /></Dropdown>
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setBlocks(blocks.filter((_, j) => j !== i))} />
                        </Space>
                    )}>
                    <BlockFields b={b} discounts={discounts} collections={collections} set={(patch) => setBlocks(blocks.map((x, j) => (j === i ? { ...x, ...patch } : x)))} />
                </Card>
            ))}
            <Dropdown menu={addMenu(blocks.length)} trigger={['click']} disabled={blocks.length >= 60}>
                <Button type="dashed" block icon={<PlusOutlined />}>{t('Add block')}</Button>
            </Dropdown>
        </div>
    );
};
